import {
  Network,
  Operation,
  Zone,
  type AddRouteRequest,
  type AddSubnetRequest,
  type AttachServerRequest,
  type CreateNetworkRequest,
  type UpdateNetworkRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";
export { progressOf, useRunningOperations } from "./operations.js";
import { keys } from "./queries.js";

/** The project's private networks; live updates refresh them through the `projects` key. */
export const useNetworks = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "networks"],
    queryFn: () => api(`/projects/${projectId}/networks`, { schema: z.array(Network) }),
  });

export const useNetwork = (projectId: string, networkId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "networks", networkId],
    queryFn: () => api(`/projects/${projectId}/networks/${networkId}`, { schema: Network }),
  });

/** Network zones of the provider (a network's subnets each live in one). */
export const useNetworkZones = (projectId: string) =>
  useQuery({
    queryKey: keys.catalogue(projectId, "zones"),
    queryFn: () => api(`/projects/${projectId}/catalogue/zones`, { schema: z.array(Zone) }),
  });

function useProjectWrite<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}

export const useCreateNetwork = (projectId: string) =>
  useProjectWrite((body: CreateNetworkRequest) =>
    api(`/projects/${projectId}/networks`, {
      method: "POST",
      body,
      schema: Network,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateNetwork = (projectId: string, networkId: string) =>
  useProjectWrite((body: UpdateNetworkRequest) =>
    api(`/projects/${projectId}/networks/${networkId}`, { method: "PATCH", body, schema: Network }),
  );

export const useDeleteNetwork = (projectId: string) =>
  useProjectWrite((networkId: string) =>
    api(`/projects/${projectId}/networks/${networkId}`, { method: "DELETE" }),
  );

const accepted = (path: string, method: "POST" | "DELETE", body?: unknown) =>
  api(path, { method, ...(body === undefined ? {} : { body }), schema: Operation });

export const useNetworkProtection = (projectId: string, networkId: string) =>
  useProjectWrite((on: boolean) =>
    accepted(`/projects/${projectId}/networks/${networkId}/actions/protection`, "POST", {
      delete: on,
    }),
  );

export const useAddSubnet = (projectId: string, networkId: string) =>
  useProjectWrite((body: AddSubnetRequest) =>
    accepted(`/projects/${projectId}/networks/${networkId}/subnets`, "POST", body),
  );

export const useDeleteSubnet = (projectId: string, networkId: string) =>
  useProjectWrite((ipRange: string) =>
    accepted(
      `/projects/${projectId}/networks/${networkId}/subnets?${new URLSearchParams({ ipRange })}`,
      "DELETE",
    ),
  );

export const useAddRoute = (projectId: string, networkId: string) =>
  useProjectWrite((body: AddRouteRequest) =>
    accepted(`/projects/${projectId}/networks/${networkId}/routes`, "POST", body),
  );

export const useDeleteRoute = (projectId: string, networkId: string) =>
  useProjectWrite((route: AddRouteRequest) =>
    accepted(
      `/projects/${projectId}/networks/${networkId}/routes?${new URLSearchParams(route)}`,
      "DELETE",
    ),
  );

export const useAttachServer = (projectId: string, networkId: string) =>
  useProjectWrite((body: AttachServerRequest) =>
    accepted(`/projects/${projectId}/networks/${networkId}/actions/attach`, "POST", body),
  );

export const useDetachServer = (projectId: string, networkId: string) =>
  useProjectWrite((serverId: string) =>
    accepted(`/projects/${projectId}/networks/${networkId}/actions/detach`, "POST", { serverId }),
  );
